import Box from "@mui/material/Box";
import MuiTypography from "@mui/material/Typography";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import type { SectionCardProps } from "../types";

const c = tokens.colour;
const sp = tokens.spacing;

/**
 * ADR-20261102-h2fd (AC-24 to AC-34): H2 screen 4&5's open section. A head with the counter, the
 * section title, an optional policy reference and, on the right, the section's own text action
 * (Assign to a colleague, or Reassign); then the body, 16px between its blocks.
 */
export function SectionCard({ counter, title, policyRef, action, headExtra, titleAction, children, testId }: Readonly<SectionCardProps>) {
  return (
    <Box
      {...cleanProps({ "data-testid": testId })}
      sx={{
        backgroundColor: c.neutral.white,
        borderRadius: `${tokens.interactionState.radius.surface}px`,
        boxShadow: tokens.interactionState.elevation.card,
        border: `${tokens.interactionState.border.emphasisWidth}px solid ${c.brand[100]}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          padding: { xs: `${sp.lg}px ${sp.md}px ${sp.md}px`, sm: `${sp.lg}px ${sp.lg}px ${sp.md}px` },
        }}
      >
        <MuiTypography variant="body2" sx={{ color: c.neutral.inkMuted }}>
          {counter}
        </MuiTypography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            justifyContent: "space-between",
            gap: { xs: 0, sm: `${sp.md}px` },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
            <MuiTypography variant="h3" component="h2">
              {title}
              {titleAction}
            </MuiTypography>
            {policyRef !== undefined ? (
              <MuiTypography variant="body2" sx={{ color: c.neutral.inkMuted }}>
                {policyRef}
              </MuiTypography>
            ) : null}
          </Box>
          {action !== undefined ? (
            <Box
              component="button"
              type="button"
              onClick={action.onClick}
              {...cleanProps({ "data-testid": action.testId, "aria-expanded": action.expanded })}
              sx={{
                appearance: "none",
                background: "none",
                border: "none",
                padding: "10px 0",
                margin: { xs: 0, sm: "-7px 0 0" },
                minHeight: tokens.interactionState.targetSize.standard,
                alignSelf: { xs: "flex-start", sm: "auto" },
                flex: { xs: "0 0 auto", sm: "0 1 auto" },
                minWidth: 0,
                fontFamily: "inherit",
                fontSize: 17,
                lineHeight: 1.3,
                whiteSpace: { xs: "normal", sm: "nowrap" },
                textAlign: { xs: "left", sm: "right" },
                cursor: "pointer",
                color: c.brand[700],
                textDecoration: "underline",
                textUnderlineOffset: "3px",
                "&:hover": { textDecorationThickness: "2px" },
                // H2 `style-focus`: the outline shows on any focus, a click included.
                "&:focus": { outline: `3px solid ${c.brand[700]}`, outlineOffset: "2px" },
              }}
            >
              {action.label}
            </Box>
          ) : null}
        </Box>
        {headExtra}
      </Box>
      <Box
        sx={{
          padding: { xs: `0 ${sp.md}px ${sp.md}px`, sm: `0 ${sp.lg}px ${sp.lg}px` },
          display: "flex",
          flexDirection: "column",
          gap: `${sp.md}px`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
